import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { setAuthToken, clearAuthToken, meService } from '../api/apiClient';
import { Hanko } from '@teamhanko/hanko-elements';

const HANKO_API_URL = 'https://hanko-ji2csljmda-uk.a.run.app';
const DEMO_SESSION_KEY = 'someac-demo-session';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Lazily-instantiated Hanko SDK client
let hankoClient = null;
function getHankoClient() {
  if (!hankoClient) {
    hankoClient = new Hanko(HANKO_API_URL);
  }
  return hankoClient;
}

async function requestDemoSession() {
  clearAuthToken();
  const response = await meService.openDemo();
  const data = response.data?.data || response.data;
  if (!response.success || !data?.accessToken) throw new Error(response.message || 'Demo access could not be opened. Try again.');
  setAuthToken(data.accessToken);
  const checked = await meService.getMe();
  const guest = checked.data?.data || checked.data;
  if (!checked.success || !guest?.roles?.includes('someac_guest')) throw new Error('Demo access could not be verified. Try again.');
  window.sessionStorage.setItem(DEMO_SESSION_KEY, data.accessToken);
  return { token: data.accessToken, guest };
}

/**
 * Hanko-based AuthProvider.
 * - Uses Hanko session token for API calls (Bearer header).
 * - Session persists in Hanko cookie; survives page refreshes.
 */
export function AuthProvider({ children }) {
  const [idToken, setIdToken] = useState('');
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState({ state: 'loading', message: 'Checking session…' });

  // Check for existing Hanko session on mount
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const demoEntry = window.location.pathname.replace(/\/+$/, '') === '/demo';
        const token = window.sessionStorage.getItem(DEMO_SESSION_KEY) || (!demoEntry ? getHankoClient().getSessionToken() : null);
        if (token) {
          if (cancelled) return;
          setIdToken(token);
          setAuthToken(token);

          const resp = await meService.getMe();
          if (!cancelled && resp.success) {
            setUser(resp.data?.data || resp.data);
            setStatus({ state: 'signed_in', message: '' });
            return;
          }
        }
      } catch (_) {
        // No valid session, fall through to signed_out
      }
      if (!cancelled && window.location.pathname.replace(/\/+$/, '') === '/demo') {
        try {
          const { token, guest } = await requestDemoSession();
          if (cancelled) return;
          setIdToken(token);
          setUser(guest);
          setStatus({ state: 'signed_in', message: '' });
          return;
        } catch (error) {
          if (cancelled) return;
          window.sessionStorage.removeItem(DEMO_SESSION_KEY);
          clearAuthToken();
          setIdToken('');
          setUser(null);
          setStatus({ state: 'error', mode: 'demo', message: error.message });
          return;
        }
      }
      if (!cancelled) {
        window.sessionStorage.removeItem(DEMO_SESSION_KEY);
        setIdToken('');
        clearAuthToken();
        setStatus({ state: 'signed_out', message: '' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const signOut = useCallback(async () => {
    const demoSession = window.sessionStorage.getItem(DEMO_SESSION_KEY);
    window.sessionStorage.removeItem(DEMO_SESSION_KEY);
    try {
      if (!demoSession) await getHankoClient().logout();
    } catch (_) {
      // ignore logout errors
    }
    setIdToken('');
    setUser(null);
    clearAuthToken();
    setStatus({ state: 'signed_out', message: '' });
  }, []);

  /**
   * Called after Hanko auth flow completes (hankoAuthFlowCompleted event).
   * Gets the session JWT from Hanko and validates against the backend.
   */
  const signInWithHanko = useCallback(async (externalToken) => {
    window.sessionStorage.removeItem(DEMO_SESSION_KEY);
    setStatus({ state: 'signing_in', message: 'Signing in…' });
    try {
      const hanko = getHankoClient();
      const token = externalToken || hanko.getSessionToken();

      if (!token) {
        setStatus({ state: 'error', message: 'Could not retrieve session token.' });
        return { ok: false };
      }

      setIdToken(token);
      setAuthToken(token);

      const resp = await meService.getMe();
      if (!resp.success) {
        const message = resp.data?.message || resp.message || 'Sign-in failed';
        setUser(null);
        setIdToken('');
        clearAuthToken();
        setStatus({ state: 'error', message });
        return { ok: false, error: resp.data };
      }

      setUser(resp.data?.data || resp.data);
      setStatus({ state: 'signed_in', message: '' });
      return { ok: true };
    } catch (err) {
      setStatus({ state: 'error', message: err.message || 'Sign-in failed' });
      return { ok: false };
    }
  }, []);

  const signInAsDemoGuest = useCallback(async () => {
    setStatus({ state: 'signing_in', message: 'Opening demo…' });
    clearAuthToken();
    try {
      const { token, guest } = await requestDemoSession();
      setIdToken(token);
      setUser(guest);
      setStatus({ state: 'signed_in', message: '' });
      return { ok: true };
    } catch (error) {
      window.sessionStorage.removeItem(DEMO_SESSION_KEY);
      clearAuthToken();
      setIdToken('');
      setUser(null);
      setStatus({ state: 'error', message: error.message });
      return { ok: false, error: { message: error.message } };
    }
  }, []);

  // Also support old signInWithIdToken for backward compat
  const signInWithIdToken = signInWithHanko;

  const value = useMemo(() => {
    const roles = user?.roles || [];
    const isSuperuser = roles.includes('someac_superuser');
    const isOwner = roles.includes('someac_owner');
    const isGuest = roles.includes('someac_guest');
    return {
      idToken,
      user,
      status,
      isAuthenticated: !!idToken && !!user,
      roles,
      isSuperuser,
      isOwner,
      isGuest,
      customerId: user?.customerId || null,
      signInWithIdToken,
      signInWithHanko,
      signInAsDemoGuest,
      signOut
    };
  }, [idToken, user, status, signInWithIdToken, signInWithHanko, signInAsDemoGuest, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {status.state === 'loading' ? null : children}
    </AuthContext.Provider>
  );
}
